import { HTMLAttributes, memo, useCallback } from 'react';
import { useSystemLanguageValue } from 'hooks/business/system/useSystem';

import TradeCommentView from 'components/TradeComment/TradeCommentView';
import { useCommentsTranslationController } from 'components/TradeComment/useCommentsTranslationController';
import useTradeTranslate, { getCommentTranslation } from './useTradeTranslate';

export interface TradeCommentProps extends HTMLAttributes<HTMLDivElement> {
  comment?: Conversation.Trade.Comment;
  comments: Conversation.Trade.Comment[];
  itemIndex: number;
  commentIndex: number;
  platform: string;
}

function TradeComment(props: TradeCommentProps) {
  const { comments, itemIndex, commentIndex, platform, ...otherProps } = props;
  const systemLang = useSystemLanguageValue();
  const { pending: translating, error: translationError, translateComment } = useTradeTranslate();

  const translateMissing = useCallback(
    ({ comment, commentIndex, itemIndex: injectedItemIndex }) => {
      return translateComment(
        comment,
        injectedItemIndex ?? itemIndex,
        commentIndex,
      ) as unknown as Promise<void>;
    },
    [itemIndex, translateComment],
  );

  const getTranslationFromComment = useCallback(
    (comment: Conversation.Trade.Comment) => {
      return getCommentTranslation(comment.transBuyerComment, systemLang);
    },
    [systemLang],
  );

  const controller = useCommentsTranslationController({
    comments,
    itemIndex,
    langKey: systemLang,
    getTranslationFromComment,
    translateMissing,
    translateStatus: {
      pending: translating,
      error: translationError,
    },
  });

  return (
    <TradeCommentView
      {...otherProps}
      comments={comments}
      platform={platform}
      previewText={controller.previewText}
      needTranslate={controller.needTranslate}
      displayTranslation={controller.displayTranslation}
      translations={controller.translations}
      translating={controller.translating}
      translationError={controller.translationError}
      onToggleTranslation={controller.toggleTranslation}
    />
  );
}

export default memo(TradeComment);
